import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Lightbulb, ArrowRight, Target, CheckCircle, 
  FileText, Search, GraduationCap, MessageSquare,
  User, Briefcase, X, Clock, Flag, Sparkles,
  Compass, HelpCircle, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { authenticatedFetch } from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for different tool keys
const toolIcons = {
  resume_builder: FileText,
  career_assessment: Target,
  job_search: Search,
  interview_prep: MessageSquare,
  career_planner: Compass,
  remote_work: Briefcase,
  profile: User,
  build_new_resume: FileText,
  ats_resume_check: CheckCircle,
  career_assessments: Target,
  search_jobs: Search,
  my_learning: GraduationCap,
  job_tracker: Briefcase
};

// Intent display names
const intentLabels = {
  resume_focused: 'Building Your Resume',
  clarity_seeker: 'Finding Your Direction',
  job_searcher: 'Finding Opportunities',
  interview_prep: 'Interview Preparation'
};

/**
 * GPS Start Here Card Component
 * 
 * Shows personalized "Start Here" recommendation based on GPS onboarding.
 * Falls back to contextual prompt if user hasn't completed onboarding.
 */
const GPSStartHereCard = ({ onPersonalizePath, onStartOnboarding }) => {
  const navigate = useNavigate();
  const [recommendation, setRecommendation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [hasFallback, setHasFallback] = useState(false);

  useEffect(() => {
    loadRecommendations();
  }, []);

  const loadRecommendations = async () => {
    try {
      // Check for session dismissal
      if (sessionStorage.getItem('startHereCardDismissed') === 'true') {
        setDismissed(true);
        setLoading(false);
        return;
      }

      const response = await authenticatedFetch(`${API_URL}/api/gps/recommendations`);
      if (response.ok) {
        const data = await response.json();
        setRecommendation(data);
        setHasFallback(data.fallback);
      }
    } catch (error) {
      console.debug('Failed to load GPS recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('startHereCardDismissed', 'true');
  };

  const handleStartAction = async (tool) => {
    // Track this action for encouragement
    try {
      await authenticatedFetch(`${API_URL}/api/gps/track-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_key: tool.key })
      });
    } catch (error) {
      console.debug('Failed to track action:', error);
    }
    
    // Navigate to the tool
    navigate(tool.route);
  };

  if (loading || dismissed) {
    return null;
  }

  // Fallback Card for users who haven't completed GPS onboarding
  if (hasFallback) {
    return (
      <div className="mb-8">
        <div className="relative overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-sm">
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-6 border-b">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <HelpCircle className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Not Sure Where to Start?</h2>
                  <p className="text-gray-600 text-sm mt-1">That&apos;s okay. Most people don&apos;t have everything figured out yet.</p>
                </div>
              </div>
              <button 
                onClick={handleDismiss}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6">
            <p className="text-gray-600 mb-4">
              Here are a few good places to begin. You can&apos;t choose wrong.
            </p>
            
            {/* Static Entry Options */}
            <div className="space-y-3">
              {recommendation?.fallback_options?.map((tool, index) => {
                const Icon = toolIcons[tool.key] || FileText;
                return (
                  <button
                    key={tool.key}
                    onClick={() => handleStartAction(tool)}
                    className="w-full p-4 bg-gray-50 hover:bg-blue-50 rounded-xl border border-gray-200 hover:border-blue-200 text-left transition-all group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm group-hover:shadow">
                        <Icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{tool.title}</h3>
                        <p className="text-sm text-gray-500">{tool.description}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600" />
                    </div>
                  </button>
                );
              })}
            </div>
            
            {/* Personalize CTA */}
            <div className="mt-6 pt-6 border-t border-gray-100">
              <p className="text-sm text-gray-500 mb-3">
                Answer a few quick questions to get personalized recommendations.
              </p>
              {onStartOnboarding && (
                <Button 
                  onClick={onStartOnboarding}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  <Compass className="w-4 h-4 mr-2" />
                  Personalize My Experience
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Personalized Start Here Card
  const primaryAction = recommendation?.primary_action;
  if (!primaryAction) return null;

  const Icon = toolIcons[primaryAction.key] || Lightbulb;
  const userIntent = recommendation?.user_intent;
  const intentLabel = intentLabels[userIntent] || 'Your Career Journey';

  return (
    <div className="mb-8">
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-32 translate-x-32" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-24 -translate-x-24" />
        </div>
        
        {/* Dismiss Button */}
        <button 
          onClick={handleDismiss}
          className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-full transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* Content */}
        <div className="relative">
          {/* Label */}
          <div className="flex items-center gap-2 mb-4">
            <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
              Recommended for You
            </span>
            <span className="text-blue-200 text-sm">
              Step 1 of Your Career Reset
            </span>
          </div>
          
          {/* Header */}
          <div className="flex items-start gap-4 mb-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0">
              <Icon className="w-7 h-7" />
            </div>
            <div>
              <p className="text-blue-100 text-sm mb-1">START HERE</p>
              <h2 className="text-2xl font-bold">{primaryAction.title}</h2>
            </div>
          </div>
          
          {/* Description */}
          <p className="text-blue-100 mb-4">
            Based on your answers, this is the best first step right now.
          </p>
          
          {/* Outcome Indicators */}
          <div className="flex flex-wrap gap-4 mb-6 text-sm">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-200" />
              <span>⏱ {primaryAction.time_estimate || '10-15 minutes'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Flag className="w-4 h-4 text-blue-200" />
              <span>🏁 {primaryAction.outcome || 'One meaningful career step completed'}</span>
            </div>
          </div>
          
          {/* CTA Button */}
          <Button 
            onClick={() => handleStartAction(primaryAction)}
            size="lg"
            className="bg-white text-blue-600 hover:bg-blue-50 shadow-lg"
          >
            Start This Step
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          
          {/* Reassurance */}
          <p className="text-blue-200 text-sm mt-4">
            You can explore everything else anytime. Nothing is locked.
          </p>
          
          {/* Personalize My Path - Always visible */}
          {onPersonalizePath && (
            <div className="mt-4 pt-4 border-t border-white/20">
              <button 
                onClick={onPersonalizePath}
                className="text-blue-100 hover:text-white text-sm flex items-center gap-2 transition-colors"
              >
                <Compass className="w-4 h-4" />
                Personalize My Path (Update Recommendations)
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Coaching Hint (conditional) */}
      {recommendation?.coaching_interested && (
        <div className="mt-3 p-4 bg-purple-50 rounded-xl border border-purple-100">
          <p className="text-purple-700 text-sm flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Want extra guidance later? Coaching support feature is coming soon.
          </p>
        </div>
      )}
    </div>
  );
};

export default GPSStartHereCard;
